import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { InputDropdown } from "./InputDropdown";

function SortableTopicRow({
  fields,
  field,
  ind,
  register,
  errors,
  getValues,
  setValue,
  openDropdownIndex,
  setOpenDropdownIndex,
  inputValues,
  handleRemoveTopic,
  expanded
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: field.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const promptRef = useRef(null);
  const promptField = register(`topics.${ind}.prompt`, {
    required: "Prompt is required",
  });

  // Grow the textarea to fit its full content (used on type + when "Expand all" is on).
  const growToContent = () => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  // Sync height whenever the global expand/collapse toggle flips.
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    if (expanded) {
      growToContent();
    } else {
      el.style.height = "";
    }
  }, [expanded]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative bg-white rounded-ns border border-rule   transition-all duration-200"
    >
      {/* HEADER STRIP */}
      <div className="flex items-center gap-2 p-2 border-b border-rule bg-shade-50/80 rounded-t-ns">

        {/* DRAG HANDLE */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-muted hover:text-muted"
        >
          <GripVertical size={18} />
        </div>

        {/* TOPIC NAME FIELD */}
        <div className="flex-1">
          <input
            {...register(`topics.${ind}.name`, {
              required: "Topic Name is required",
              pattern: {
                value: /^(?=.*[A-Za-z]).+$/,
                message: "Topic Name must contain at least one alphabet letter",
              },
            })}
            placeholder="Topic Name..."
            className="w-full bg-transparent font-bold text-ink text-base placeholder-shade-900 
                       focus:outline-none focus:ring-2 focus:ring-accent rounded px-1.5 py-0.5"
          />
          {errors?.topics?.[ind]?.name && (
            <p className="text-danger text-xs mt-1">{errors.topics[ind].name.message}</p>
          )}
        </div>

        {/* DELETE BUTTON */}
        <button
          type="button"
          onClick={() => handleRemoveTopic(ind)}
          className="p-1 text-muted hover:text-danger hover:bg-danger/10 rounded transition-colors"
        >
          <Trash2 size={18} />
        </button>
      </div>

      {/* BODY CONTENT */}
      <div className="p-3 grid grid-cols-1 md:grid-cols-12 gap-3">

        {/* SUMMARY TYPE DROPDOWN */}
        <div className="md:col-span-4 lg:col-span-3">
          <InputDropdown
            value={getValues(`topics.${ind}.summarizationType`) || "Points"}
            onChange={(val) => {
              setValue(`topics.${ind}.summarizationType`, val, { shouldValidate: true });
            }}
            InputValues={inputValues}
            open={openDropdownIndex === ind}
            setOpen={(isOpen) => setOpenDropdownIndex(isOpen ? ind : null)}
          />

          {errors?.topics?.[ind]?.summarizationType && (
            <p className="text-danger text-xs mt-1">
              {errors.topics[ind].summarizationType.message}
            </p>
          )}


          {errors?.topics?.[ind]?.summarizationType && (
            <p className="text-danger text-xs mt-1">
              {errors.topics[ind].summarizationType.message}
            </p>
          )}
        </div>

        {/* PROMPT FIELD */}
        <div className="md:col-span-8 lg:col-span-9">

          <textarea
            {...promptField}
            ref={(el) => {
              promptField.ref(el);
              promptRef.current = el;
            }}
            onInput={growToContent}
            placeholder="Instructions for AI..."
            className="w-full bg-shade-50 border border-rule outline-none text-ink py-2 px-3 rounded-ns
                       focus:outline-none focus:ring-1 focus:ring-accent focus:bg-white text-sm
                       min-h-[48px] resize-y"
          />

          {errors?.topics?.[ind]?.prompt && (
            <p className="text-danger text-xs mt-1">{errors.topics[ind].prompt.message}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default SortableTopicRow;
